// src/data/storyAssessmentData.js — the Story Threads Vibe check.
//
// Sixteen questions across five dimensions:
//   knowing  — how much of your family's history you actually hold
//   asking   — whether you ask, and how easy asking feels
//   telling  — whether you can tell one of these stories well
//   keeping  — whether any of it is written down or recorded
//   hard     — how your family handles the difficult ones
//
// The five knowledge questions are modelled on the "Do You Know?" scale
// (Duke, Lazarus & Fivush, 2008) — the idea, not their wording — and they do
// double duty: every one you answer "no idea" to becomes a question to go and
// ask someone, which is the most useful thing this check produces.
//
// ⚠️ No question is scored as a personal failure. A low score means the
// stories are still in other people's heads, which is the normal starting
// point and the easiest thing here to change. See the stage copy below.
//
// Option order on screen is decided per attempt (see utils/optionOrder.js),
// so the strongest answer is never reliably in the same spot: ordered scales
// keep their sequence but may run in either direction, and questions marked
// optionStyle: 'shuffle' (distinct behaviours, not a scale) are shuffled
// outright. Because of that, every option's text must stand on its own —
// no option may refer to the one above it.

export const STORY_ASSESSMENT_VERSION = 1;

export const storyAssessmentQuestions = [
  // ---------- Knowing ----------
  {
    id: 'know-parents-met',
    category: 'What you know',
    text: 'Do you know how your parents, or the adults who raised you, met?',
    ask: 'How did you two meet?',
    options: [
      { text: 'No idea', value: 0 },
      { text: "I've heard bits of it", value: 1 },
      { text: 'Roughly, not the details', value: 2 },
      { text: 'Yes, the short version', value: 3 },
      { text: 'Yes, and I could tell it properly', value: 4 },
    ],
    dimensionWeights: { knowing: 1 },
  },
  {
    id: 'know-grandparents-grew-up',
    category: 'What you know',
    text: 'Do you know where your grandparents grew up?',
    ask: 'What was the place you grew up in like?',
    options: [
      { text: 'No idea', value: 0 },
      { text: 'One of them, vaguely', value: 1 },
      { text: 'The countries or towns, not much else', value: 2 },
      { text: 'Yes, and something about what it was like', value: 3 },
      { text: 'Yes, and I could describe their street', value: 4 },
    ],
    dimensionWeights: { knowing: 1 },
  },
  {
    id: 'know-hard-time',
    category: 'What you know',
    text: 'Do you know a story about something hard your family came through?',
    ask: 'What is the hardest thing this family has come through?',
    options: [
      { text: 'No', value: 0 },
      { text: 'I know something happened, not the story', value: 1 },
      { text: 'Roughly', value: 2 },
      { text: 'Yes', value: 3 },
      { text: 'Yes, including what it cost them', value: 4 },
    ],
    dimensionWeights: { knowing: 1, hard: 0.5 },
  },
  {
    id: 'know-how-you-got-here',
    category: 'What you know',
    text: 'Do you know how your family came to live where you live now?',
    ask: 'How did we end up living here?',
    options: [
      { text: 'No idea', value: 0 },
      { text: "I've heard a version of it", value: 1 },
      { text: 'Roughly', value: 2 },
      { text: 'Yes', value: 3 },
      { text: 'Yes, including who decided and why', value: 4 },
    ],
    dimensionWeights: { knowing: 1 },
  },
  {
    id: 'know-someone-never-met',
    category: 'What you know',
    text: 'Do you know a story about a relative you never met?',
    ask: 'Tell me about someone in this family I never got to meet.',
    options: [
      { text: 'No', value: 0 },
      { text: 'Only their name', value: 1 },
      { text: 'A fact or two', value: 2 },
      { text: 'Yes, one story', value: 3 },
      { text: 'Yes, more than one', value: 4 },
    ],
    dimensionWeights: { knowing: 0.8 },
  },

  // ---------- Asking ----------
  {
    id: 'ask-how-often',
    category: 'Asking',
    text: 'When you are with older relatives, how often do you ask them about their life?',
    options: [
      { text: 'Never', value: 0 },
      { text: 'Almost never', value: 1 },
      { text: 'Now and then', value: 2 },
      { text: 'Most times I see them', value: 3 },
      { text: 'Always, it is how we talk', value: 4 },
    ],
    dimensionWeights: { asking: 1 },
  },
  {
    id: 'ask-how-it-feels',
    category: 'Asking',
    text: 'How does it feel to ask a relative about their past?',
    options: [
      { text: 'I would not know how to start', value: 0 },
      { text: 'Awkward', value: 1 },
      { text: 'Fine if there is a reason for it', value: 2 },
      { text: 'Easy enough', value: 3 },
      { text: 'Easy, and they enjoy it', value: 4 },
    ],
    dimensionWeights: { asking: 1 },
  },
  {
    id: 'ask-repeat-story',
    category: 'Asking',
    text: 'A relative starts a story you have heard before. What do you usually do?',
    optionStyle: 'shuffle',
    options: [
      { text: 'Tune out', value: 0 },
      { text: 'Wait for it to finish', value: 1 },
      { text: 'Listen properly', value: 2 },
      { text: 'Ask a question about a part I never understood', value: 3 },
      { text: 'Ask what happened next, or what it felt like', value: 4 },
    ],
    dimensionWeights: { asking: 0.8, telling: 0.3 },
  },

  // ---------- Telling ----------
  {
    id: 'tell-best-story',
    category: 'Telling',
    text: "Could you tell your family's best story to someone who has never met them?",
    options: [
      { text: 'I could not name one', value: 0 },
      { text: 'I know which one, but I would make a mess of it', value: 1 },
      { text: 'Roughly, with the good bits missing', value: 2 },
      { text: 'Yes, well enough', value: 3 },
      { text: 'Yes, and it lands every time', value: 4 },
    ],
    dimensionWeights: { telling: 1 },
  },
  {
    id: 'tell-whats-in-it',
    category: 'Telling',
    text: 'When you tell a family story, what is usually in it?',
    optionStyle: 'shuffle',
    options: [
      { text: 'Just the events', value: 0 },
      { text: 'The events, and how things turned out', value: 1 },
      { text: 'How people felt while it was happening', value: 2 },
      { text: 'What it cost the people in it', value: 3 },
      { text: 'The whole shape: what it cost, and what the family learned', value: 4 },
    ],
    dimensionWeights: { telling: 1, hard: 0.4 },
  },
  {
    id: 'tell-to-younger',
    category: 'Telling',
    text: 'Has anyone younger than you heard a family story from you?',
    options: [
      { text: 'No', value: 0 },
      { text: 'Once, in passing', value: 1 },
      { text: 'A couple of times', value: 2 },
      { text: 'Yes, and they asked for more', value: 3 },
      { text: 'Yes, it is a regular thing', value: 4 },
    ],
    dimensionWeights: { telling: 0.8, keeping: 0.3 },
  },

  // ---------- Keeping ----------
  {
    id: 'keep-anything-recorded',
    category: 'Keeping',
    text: 'Is any of it recorded — voice notes, video, or written down?',
    options: [
      { text: 'Nothing', value: 0 },
      { text: 'Some photos, no stories', value: 1 },
      { text: 'A few notes or messages', value: 2 },
      { text: 'One or two proper recordings', value: 3 },
      { text: 'Quite a bit, and I know where it is', value: 4 },
    ],
    dimensionWeights: { keeping: 1 },
  },
  {
    id: 'keep-if-lost',
    category: 'Keeping',
    text: 'If your oldest relative could not tell their stories next year, what would be kept?',
    options: [
      { text: 'Nothing', value: 0 },
      { text: 'A few photos nobody can explain', value: 1 },
      { text: 'Photos, and the basic facts', value: 2 },
      { text: 'Some of the stories, in other people', value: 3 },
      { text: 'Most of it, recorded or written', value: 4 },
    ],
    dimensionWeights: { keeping: 1 },
  },
  {
    id: 'keep-family-habit',
    category: 'Keeping',
    text: 'Does your family do anything regularly that brings stories out?',
    options: [
      { text: 'No', value: 0 },
      { text: 'Only at funerals', value: 1 },
      { text: 'At big occasions', value: 2 },
      { text: 'Fairly often, when we are all together', value: 3 },
      { text: 'Yes, it is a habit we keep on purpose', value: 4 },
    ],
    dimensionWeights: { keeping: 0.8, asking: 0.3 },
  },

  // ---------- Hard ----------
  {
    id: 'hard-how-told',
    category: 'The hard ones',
    text: 'When something hard has happened, how does your family talk about it afterwards?',
    optionStyle: 'shuffle',
    options: [
      { text: 'It does not get talked about', value: 0 },
      { text: 'Only the bad part, over and over', value: 1 },
      { text: "Only the 'we got through it' part", value: 2 },
      { text: 'Both, sometimes', value: 3 },
      { text: 'Both, honestly: what it took, and what it gave', value: 4 },
    ],
    dimensionWeights: { hard: 1 },
  },
  {
    id: 'hard-avoided-subject',
    category: 'The hard ones',
    text: 'Is there a subject your family avoids?',
    options: [
      { text: 'Yes, and not talking about it hurts', value: 0, flagsSupport: true },
      { text: 'Yes, a big one', value: 1 },
      { text: 'One or two', value: 2 },
      { text: 'Not really', value: 3 },
      { text: 'No, we can talk about anything', value: 4 },
    ],
    dimensionWeights: { hard: 1 },
  },
];

// Stages by overall percentage. The low ones name the situation and the next
// move; none of them congratulates someone whose answers say otherwise.
export const STORY_STAGES = [
  {
    min: 0,
    max: 24,
    name: 'The stories are still in other people',
    summary:
      'Most of what your family has been through is held by somebody else, and none of it is written down. That is the ordinary starting point, and it is the fastest thing here to change: one conversation moves it more than anything else you could do this month.',
    recommendedModules: [1, 2],
    recommendedTools: ['story-style-finder', 'interview-kit'],
  },
  {
    min: 25,
    max: 44,
    name: 'You know the outline',
    summary:
      'You know roughly what happened to your family, without the detail that makes a story worth retelling. The gap is usually questions: you have not had a reason to ask the second one.',
    recommendedModules: [2, 4],
    recommendedTools: ['interview-kit', 'question-deck'],
  },
  {
    min: 45,
    max: 64,
    name: 'Collecting',
    summary:
      'You know real stories and you ask for more. What is missing is shape: the difference between knowing what happened and telling it so somebody else feels it.',
    recommendedModules: [3, 4],
    recommendedTools: ['ingredients-checker', 'question-deck'],
  },
  {
    min: 65,
    max: 84,
    name: 'Telling them well',
    summary:
      'You can find these stories and tell them. The open question is what survives: whether any of it is recorded, and whether the family has a habit that keeps it going without you.',
    recommendedModules: [5, 6],
    recommendedTools: ['ingredients-checker'],
  },
  {
    min: 85,
    max: 100,
    name: 'Keeping them',
    summary:
      'You hold your family\'s stories and you pass them on. The work left is the hard ones, and teaching somebody younger to do what you do.',
    recommendedModules: [7, 8],
    recommendedTools: ['interview-kit'],
  },
];

export const DIMENSION_LABELS = {
  knowing: 'Knowing',
  asking: 'Asking',
  telling: 'Telling',
  keeping: 'Keeping',
  hard: 'The hard ones',
};

export const DIMENSION_BLURBS = {
  knowing: "how much of your family's history you actually hold",
  asking: 'whether you ask, and how easy asking feels',
  telling: 'whether you can tell one of them well',
  keeping: 'whether any of it is recorded or written down',
  hard: 'how your family handles the difficult ones',
};

// What each score band actually means, per dimension. Bands are picked by
// readingFor() in utils/storyAssessmentCalculations.js: low is under 2 of 4,
// high is 3.25 and over, mid is everything between. The low copy names the
// situation and the first move; it never congratulates and never scolds.
export const DIMENSION_READINGS = {
  knowing: {
    low: "Most of your family's story is still in other people's heads. One conversation with the right relative would move this further than anything else in the app.",
    mid: 'You hold the outline — events, places, some names. The detail that makes a story retellable usually sits one follow-up question deeper than anyone asks.',
    high: 'You hold a lot of your family\'s story. The question stops being what you know and becomes what anyone else could find if you were not around to ask.',
  },
  asking: {
    low: 'Asking feels unnatural right now, which is common — it is rarely taught. A prepared question sheet takes away the hardest part, which is starting.',
    mid: 'You ask when the moment offers itself. The shift worth making is creating the moment rather than waiting for one: a planned conversation often gets further than a lucky one.',
    high: 'You ask easily, and relatives likely open up to you. That makes you the person best placed to reach the ones nobody in the family has asked yet.',
  },
  telling: {
    low: 'You may know stories you cannot yet put into words for someone else. That is a craft gap, not a knowledge gap, and craft is the more fixable of the two.',
    mid: 'You can carry a story across, with some of its power lost on the way. Tellings at this level often skip what it cost the people in it — which is often what makes a story land.',
    high: 'Your tellings land. A step worth trying next is adapting the same story for a seven-year-old and a seventy-year-old — that is Module 5.',
  },
  keeping: {
    low: 'If the people who hold the stories could not tell them next year, almost nothing would survive. One voice recording this month, with a second copy somewhere safe, changes that.',
    mid: 'Some of it is captured, in pieces. What is usually missing is a habit: a fixed occasion where recording happens without anyone having to decide it each time.',
    high: 'Things are being kept, on purpose. Worth checking: does anyone else know where it all is, and would it survive a lost phone?',
  },
  hard: {
    low: 'The hard chapters go untold, or get told one-sided, in your family. Told honestly, and when people are ready, those stories can matter a great deal — Module 7 is about doing that safely.',
    mid: 'Your family can touch the hard stories sometimes. The full version — what it took and what it gave — is the kind psychologist Marshall Duke calls the healthiest. In the research it goes with wellbeing; that is a link, not a guarantee.',
    high: 'Your family can talk about its difficult parts honestly. That is not easy, and it is the foundation the other four parts stand on.',
  },
};
